import eol from 'eol';
import { formatPlainText } from './formatters.js';
import { buildCueRuns, runsToPlainText } from './buildCueContent.js';

/**
 * @typedef {Object} TranscriptBlock
 * @property {'plain' | 'cue' | 'prose'} kind
 * @property {string[]} [lines]
 * @property {import('./buildCueContent.js').TranscriptRun[]} [runs]
 */

/**
 * @param {object} eventGroup
 * @param {import('./options.js').TranscriptExportOptions} options
 * @param {{ frameRate: number|string, dropFrame: boolean }} project
 * @param {import('./formatters.js').TranscriptLabelSet} labels
 * @returns {TranscriptBlock[]}
 */
export function buildCuedBlocks(eventGroup, options, project, labels) {
    const events = (eventGroup.events || [])
        .filter((event) => formatPlainText(event.text))
        .sort((a, b) => a.start - b.start);

    return events.map((event, count) => ({
        kind: 'cue',
        runs: buildCueRuns({
            event,
            index: count + 1,
            options,
            project,
            labels,
            eventGroupType: eventGroup.type,
        }),
    }));
}

/**
 * Serialize cued/prose/plain blocks to a plain-text string.
 * @param {TranscriptBlock[]} blocks
 * @returns {string}
 */
export function blocksToPlainText(blocks) {
    const parts = [];

    for (const block of blocks) {
        if (block.kind === 'plain') {
            for (const line of block.lines || []) {
                parts.push(eol.after(line));
            }
            parts.push('\n');
            continue;
        }

        if (block.kind === 'cue' || block.kind === 'prose') {
            const text = runsToPlainText(block.runs || []);
            if (text) parts.push((text + '\n').replace(/\n{3,}$/, '\n\n'));
            else parts.push('\n');
        }
    }

    return parts.join('').replace(/\n+$/, '') + '\n';
}

/**
 * @param {{ id?: string, name?: string, username?: string }} project
 * @param {Array<{ type?: string, value?: string }>} metadataState
 * @param {import('./formatters.js').TranscriptLabelSet} labels
 * @returns {TranscriptBlock}
 */
export function buildMetadataHeaderBlock(project, metadataState, labels) {
    const lines = [
        `ID: ${project.id || ''} | Name: ${project.name || ''} | Author: ${project.username || ''}`,
        labels.metadataHeader,
    ];

    if (!metadataState || metadataState.length === 0) {
        lines.push(labels.metadataNone);
    } else {
        for (const metadata of metadataState) {
            lines.push(`${metadata.type}: ${metadata.value}`);
        }
    }

    return { kind: 'plain', lines };
}
